import { LockKeyhole } from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";

import { IgTooltip } from "@intelligems/ig-theme";
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { MainRoute } from "@src/types/navigation";
import { mpSideNavClick } from "@src/utils/mixpanel";

import { activeRoute } from "../../Utils/helpers";

const PREFIX = "IgListItemLink";

const classes = {
  listItem: `${PREFIX}-listItem`,
};

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "$collapsed",
})<{ $collapsed?: boolean }>(({ theme }) => ({
  marginBottom: theme.spacing(1),
  "& .MuiListItemButton-root.Mui-selected": {
    backgroundColor: theme.igBackgroundColors.interactive.neutral.active,
  },
  "& .MuiListItemButton-root": {
    borderRadius: "8px",
    height: "32px",
    color: theme.igBackgroundColors.interactive.neutral.focused,
  },
  "& .MuiListItemIcon-root": {
    paddingRight: theme.spacing(2),
    minWidth: "16px",
  },
  "& .MuiTouchRipple-child": {
    backgroundColor: theme.igBackgroundColors.interactive.neutral.focused,
  },
  [`& .${classes.listItem}`]: {
    borderRadius: "8px",
    color: "purple",
    "&:hover": {
      backgroundColor: theme.igBackgroundColors.interactive.neutral.hover,
    },
  },
  variants: [
    {
      props: ({ $collapsed }) => $collapsed,
      style: {
        "& .MuiListItemButton-root": {
          display: "flex !important",
          justifyContent: "center !important",
          alignItems: "center !important",
          padding: "0 !important",
        },
      },
    },
    {
      props: ({ $collapsed }) => $collapsed,
      style: {
        "& .MuiListItemIcon-root": {
          padding: "0 !important",
          justifyContent: "center !important",
          display: "flex !important",
        },
      },
    },
  ],
}));

interface ListItemLinkProps {
  handleClick?: (event?: React.MouseEvent) => void;
  drawerOpen: boolean;
  Route: Pick<
    MainRoute,
    "icon" | "sidebarName" | "path" | "shortcut" | "tooltipShortcut"
  >;
  exact?: boolean;
  hasAccess: boolean;
  isLink?: boolean;
  isSubRoute?: boolean;
  chip?: React.ReactNode;
  endAdornment?: React.ReactNode;
  sx?: SxProps<Theme>;
  currentHash?: string;
  selectedAcceptedRoute?: string;
}

export const IgListItemLink = ({
  handleClick,
  Route,
  exact,
  drawerOpen,
  hasAccess,
  isLink = true,
  isSubRoute = false,
  chip,
  endAdornment,
  sx,
  currentHash,
  selectedAcceptedRoute,
}: ListItemLinkProps) => {
  const { pathname, hash } = useLocation();
  const theme = useTheme();
  const isAdmin = useIsAdmin();

  // Use currentHash prop if provided, otherwise fall back to React Router's hash
  const effectiveHash = currentHash ?? hash;
  const fullPath = pathname + effectiveHash;

  const getTooltipContent = () => {
    if (drawerOpen) {
      return "";
    }

    let shortcut = "";
    if (isAdmin && Route.tooltipShortcut) {
      shortcut = Route.tooltipShortcut;
    }

    if (shortcut) {
      return (
        <>
          {Route.sidebarName}{" "}
          <Box
            component="span"
            sx={{ border: ".5px solid white", borderRadius: "2px" }}
            px={0.5}
            ml={1}
          >
            {shortcut}
          </Box>
        </>
      );
    }

    return Route.sidebarName;
  };

  const content = () => {
    return (
      <IgTooltip placement={"right"} title={getTooltipContent()}>
        <StyledListItem
          disablePadding
          className={classes.listItem}
          $collapsed={!drawerOpen}
        >
          <ListItemButton
            disabled={!hasAccess}
            selected={activeRoute({
              routeName: Route.path,
              pathname: fullPath,
              exact,
              selectedAcceptedRoute,
            })}
            onClick={(e) => handleClick && handleClick(e)}
            sx={sx}
          >
            {Route.icon && (isSubRoute ? !drawerOpen : true) && (
              <ListItemIcon sx={{ color: theme.igIconColors.light }}>
                {hasAccess ? Route.icon : <LockKeyhole size={18} />}
              </ListItemIcon>
            )}
            {drawerOpen ? (
              <ListItemText
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {Route.sidebarName}
                    {chip}
                  </Box>
                }
                secondary={Route.shortcut}
              />
            ) : null}
            {drawerOpen ? endAdornment : null}
          </ListItemButton>
        </StyledListItem>
      </IgTooltip>
    );
  };

  if (!isLink) {
    return content();
  }
  return (
    <Link
      onClick={() => {
        mpSideNavClick(Route.sidebarName, Route.path);
      }}
      key={Route.path}
      style={{ textDecoration: "none" }}
      to={hasAccess ? Route.path : window.location.pathname}
      state={hasAccess ? { name: Route.sidebarName.toLowerCase() } : {}}
    >
      {content()}
    </Link>
  );
};
